import React, { useCallback } from "react";
import { IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DoctorconsoleGrid, StyledSuggestionButton } from "../StyledComponents";
import { useTheme, alpha } from "@mui/material/styles";

const Suggestions = React.memo(
  ({
    sectionName,
    suggestions,
    aiSuggestionToggle,
    ruleSuggestionToggle,
    handleCrossButtonClick,
    handleSuggestionClick,
  }) => {
    const theme = useTheme();

    const memoizedHandleCrossButtonClick = useCallback(
      (suggestionName, type) => {
        handleCrossButtonClick(sectionName, suggestionName, type);
      },
      [handleCrossButtonClick, sectionName]
    );

    const renderSuggestions = (suggestionsList, type, color = "primary") => {
      const mainColor = theme.palette[color]
        ? theme.palette[color].main
        : color;

      const fadedBg = alpha(mainColor, 0.15); // faded background
      const borderCol = alpha(mainColor, 0.4);

      return suggestionsList.map((suggestion, idx) => (
        <DoctorconsoleGrid
          item
          key={idx}
          sx={{ display: "inline-flex", m: 0.5 }}
        >
          {!suggestion.removed && (
            <StyledSuggestionButton
              variant="contained"
              disabled={suggestion.activate}
              onClick={() => handleSuggestionClick(suggestion.name)}
              sx={{
                textTransform: "none",
                padding: "0.6rem 1rem",
                minWidth: "7rem",
                borderRadius: "12px",
                position: "relative",
                justifyContent: "center",
                bgcolor: fadedBg,
                border: `1px solid ${borderCol}`,
                color: "#000 !important", // force black text
                "&:hover": {
                  bgcolor: alpha(mainColor, 0.25),
                },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {/* Close Button */}
              <IconButton
                size="small"
                aria-label={`remove ${suggestion.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  memoizedHandleCrossButtonClick(suggestion.name, type);
                }}
                sx={{
                  position: "absolute",
                  top: 1,
                  right: 1,
                  p: "2px",
                  color: "#000",
                }}
              >
                <CloseIcon sx={{ fontSize: "1rem" }} />
              </IconButton>

              {/* Tooltip wraps only the text */}
              <Tooltip
                title={
                  suggestion?.reason
                    ? `Reason: ${suggestion?.reason}`
                    : suggestion.name
                }
              >
                <span>{suggestion.name}</span>
              </Tooltip>
            </StyledSuggestionButton>
          )}
        </DoctorconsoleGrid>
      ));
    };

    return (
      <div style={{ marginBottom: "0.5rem" }}>
        <DoctorconsoleGrid
          container
          spacing={1}
          alignItems="flex-start"
          sx={{ backgroundColor: "#E9FAF9" }}
        >
          {suggestions[sectionName] &&
            sectionName === "Chief Complaints" &&
            renderSuggestions(
              suggestions[sectionName].aiDriven,
              "aiDriven",
              "primary"
            )}

          {suggestions[sectionName] &&
            sectionName !== "Examinations" &&
            sectionName !== "Medications" &&
            sectionName !== "Chief Complaints" &&
            sectionName !== "Procedures" &&
            sectionName !== "Attachments" &&
            ruleSuggestionToggle &&
            renderSuggestions(
              suggestions[sectionName].ruleDriven,
              "ruleDriven",
              "success"
            )}

          {suggestions[sectionName] &&
            sectionName !== "Examinations" &&
            sectionName !== "Medications" &&
            sectionName !== "Chief Complaints" &&
            sectionName !== "Procedures" &&
            sectionName !== "Attachments" &&
            aiSuggestionToggle &&
            renderSuggestions(
              suggestions[sectionName].aiDriven,
              "aiDriven",
              "secondary"
            )}
        </DoctorconsoleGrid>
      </div>
    );
  }
);

export default Suggestions;
